import { FREIGHTER_ID } from '@creit.tech/stellar-wallets-kit/modules/freighter';
import { Horizon } from '@stellar/stellar-sdk';
import { useEffect, useLayoutEffect, useRef } from 'react';

import { LS_FREIGHTER_ACCOUNT_CHANGE_IMMEDIATELY } from 'constants/local-storage';
import { AppRoutes } from 'constants/routes';
import { LOBSTR_CONNECTION_KEY } from 'constants/session-storage';

import PromisedTimeout from 'helpers/promised-timeout';

import useAssetsStore from 'store/assetsStore/useAssetsStore';
import { clearSavedAuthData, getSavedAuthData } from 'store/authStore/auth-helpers';
import { LoginTypes } from 'store/authStore/types';
import useAuthStore from 'store/authStore/useAuthStore';

import { AssetsEvent } from 'services/assets.service';
import { AuthEvent } from 'services/auth/events/events';
import {
    AssetsService,
    AuthService,
    ModalService,
    SorobanService,
    StellarService,
    ToastService,
} from 'services/globalServices';
import { StellarEvents } from 'services/stellar/events/events';

import { TokenType } from 'types/token';

import FreighterAccountChangedModal from 'web/modals/FreighterAccountChangedModal';

import { useSkipFirstRender } from './useSkipFirstRender';

const UnfundedErrors = ['Request failed with status code 404', 'Not Found'];

export default function useGlobalSubscriptions(): void {
    const {
        login,
        logout,
        resolveFederation,
        account,
        isLogged,
        updateAccount,
        loginErrorText,
        clearLoginError,
        enableRedirect,
    } = useAuthStore();

    const { processNewAssets } = useAssetsStore();

    const accountRef = useRef(account);
    const accountId = isLogged ? account?.accountId() : undefined;

    useLayoutEffect(() => {
        // Stream callbacks can arrive between a logout commit and effect cleanup.
        accountRef.current = isLogged ? account : null;
    }, [account, isLogged]);

    useEffect(() => {
        const { pubKey, loginType, walletKitId, lobstrConnectionKey, bipPath } = getSavedAuthData();

        if (!loginType) {
            return;
        }

        if (loginType === LoginTypes.walletKit) {
            if (!AuthService.walletKit.restoreLogin(walletKitId, pubKey)) {
                clearSavedAuthData();
                return;
            }
            login({ pubKey, loginType, walletKitId });

            return;
        }

        if (loginType === LoginTypes.walletConnect) {
            AuthService.walletConnect.onAppStart(
                AppRoutes.page.walletConnect.startsWith(window.location.pathname),
            );
            return;
        }

        if (loginType === LoginTypes.secret) {
            clearSavedAuthData();
            return;
        }

        if (loginType === LoginTypes.ledger) {
            AuthService.ledger.reLogin(bipPath, pubKey);
            return;
        }

        if (loginType === LoginTypes.lobstr) {
            // save connection key to session storage
            sessionStorage.setItem(LOBSTR_CONNECTION_KEY, lobstrConnectionKey);
        }

        login({ pubKey, loginType });
    }, []);

    const changeFreighterAccount = async (publicKey: string): Promise<void> => {
        ModalService.closeAllModals();
        await PromisedTimeout(500);
        const path = `${location.pathname}${location.search}`;
        logout();

        await PromisedTimeout(500);

        enableRedirect(path);
        login({ pubKey: publicKey, loginType: LoginTypes.walletKit, walletKitId: FREIGHTER_ID });
        AuthService.walletKit.restoreLogin(FREIGHTER_ID, publicKey);
    };

    useEffect(() => {
        const unsub = AuthService.event.sub(event => {
            // LOGIN
            if (event.type === AuthEvent.ledgerLogin) {
                login({
                    pubKey: event.publicKey,
                    loginType: LoginTypes.ledger,
                    bipPath: event.bipPath,
                });
            }
            if (event.type === AuthEvent.walletConnectLogin) {
                login({
                    pubKey: event.publicKey,
                    loginType: LoginTypes.walletConnect,
                    metadata: event.metadata,
                    topic: event.topic,
                });
            }
            if (event.type === AuthEvent.lobstrExtensionLogin) {
                login({
                    pubKey: event.publicKey,
                    loginType: LoginTypes.lobstr,
                });
            }

            if (event.type === AuthEvent.walletKitLogin) {
                login({
                    pubKey: event.publicKey,
                    loginType: LoginTypes.walletKit,
                    walletKitId: event.id,
                });
            }

            // LOGOUT
            if (event.type === AuthEvent.walletConnectLogout) {
                ModalService.closeAllModals();
                PromisedTimeout(500).then(() => {
                    logout();
                });
            }
            if (event.type === AuthEvent.ledgerLogout) {
                logout();
            }
            if (event.type === AuthEvent.walletKitLogout) {
                ModalService.closeAllModals();
                logout();
            }

            // OTHER

            if (event.type === AuthEvent.walletKitAccountChanged) {
                const defaultChoice = JSON.parse(
                    localStorage.getItem(LS_FREIGHTER_ACCOUNT_CHANGE_IMMEDIATELY),
                );

                if (defaultChoice === null) {
                    ModalService.closeAllModals();
                    ModalService.openModal(FreighterAccountChangedModal, {
                        publicKey: event.publicKey,
                    });
                    return;
                }

                if (defaultChoice) {
                    changeFreighterAccount(event.publicKey);
                }
            }
        });

        return () => unsub();
    }, []);

    useEffect(() => {
        const unsub = AssetsService.event.sub(event => {
            if (event.type === AssetsEvent.newAssets) {
                processNewAssets(event.payload);
            }
        });

        return () => unsub();
    }, []);

    useEffect(() => {
        if (loginErrorText) {
            ToastService.showErrorToast(
                UnfundedErrors.includes(loginErrorText)
                    ? 'Deposit at least 1 XLM to activate the account first.'
                    : loginErrorText,
            );
            clearLoginError();
        }
        if (UnfundedErrors.includes(loginErrorText)) {
            AuthService.walletConnect.logout();
            logout();
        }
    }, [loginErrorText]);

    useSkipFirstRender(() => {
        if (isLogged) {
            if (account.home_domain) {
                resolveFederation(account.home_domain, account.accountId());
            }
            ToastService.showSuccessToast('Logged in');
        } else {
            SorobanService.connection.logoutWithSecret();
            AuthService.walletKit.logout();
            ToastService.showSuccessToast('Logged out');
        }
    }, [isLogged]);

    useEffect(() => {
        if (!accountId) return;

        StellarService.account.startAccountStream(accountId);
        return () => StellarService.account.closeAccountStream();
    }, [accountId]);

    useEffect(() => {
        if (account) {
            account.getSortedBalances().then(res => {
                const classicTokens = res.filter(({ token }) => token.type === TokenType.classic);
                processNewAssets(classicTokens.map(({ token }) => token));
            });
            return;
        }
    }, [account]);

    useEffect(() => {
        const unsub = StellarService.event.sub(({ type, account: newAccount }) => {
            const currentAccount = accountRef.current;

            // Ignore queued stream/transaction updates after logout or a wallet switch.
            if (
                !currentAccount ||
                !newAccount ||
                currentAccount.accountId() !== newAccount.account_id
            ) {
                return;
            }

            if (
                type === StellarEvents.handleAccountUpdate ||
                (type === StellarEvents.accountStream &&
                    StellarService.account.balancesHasChanges(
                        currentAccount.balances as Horizon.HorizonApi.BalanceLineAsset[],
                        newAccount.balances as Horizon.HorizonApi.BalanceLineAsset[],
                    ))
            ) {
                updateAccount(newAccount, currentAccount.authType);
            }
        });

        return () => unsub();
    }, []);
}
